// Frontend helpers and configuration for Property Structure across:
// Residential, Commercial, Hybrid (Residential + Commercial), and Industrial.

export const PROPERTY_CATEGORIES = {
  RESIDENTIAL: "Residential",
  COMMERCIAL: "Commercial",
  HYBRID: "Hybrid (Residential + Commercial)",
  INDUSTRIAL: "Industrial",
};

export const PROPERTY_STRUCTURE_CONFIG = {
  [PROPERTY_CATEGORIES.RESIDENTIAL]: {
    category: PROPERTY_CATEGORIES.RESIDENTIAL,
    label: "Residential",
    subdivisionLabel: "Tower / Wing",
    subdivisionPlural: "Towers / Wings",
    levelLabel: "Floor",
    unitLabel: "Flat",
    unitPlural: "Flats",
    defaultStartFloor: 1,
    supportsGroundFloor: true,
    supportsBasements: true,
    defaultUnitType: "1BHK",
    standardUnitTypes: [
      "1BHK",
      "2BHK",
      "3BHK",
      "4BHK",
      "5BHK",
      "Studio",
      "Penthouse",
      "Duplex",
      "Row House",
      "Villa",
      "Jodi",
    ],
  },
  [PROPERTY_CATEGORIES.COMMERCIAL]: {
    category: PROPERTY_CATEGORIES.COMMERCIAL,
    label: "Commercial",
    subdivisionLabel: "Commercial Wing / Block",
    subdivisionPlural: "Commercial Wings / Blocks",
    levelLabel: "Floor",
    unitLabel: "Shop / Store",
    unitPlural: "Shops / Stores",
    defaultStartFloor: 0,
    supportsGroundFloor: true,
    supportsBasements: true,
    defaultUnitType: "Shop",
    standardUnitTypes: [
      "Shop",
      "Retail Store",
      "Showroom",
      "Duplex Store (Multi-Storey)",
      "Anchor Store",
      "Boutique",
      "Food Court / Cafe",
      "Pharmacy / Chemist",
      "Commercial Office",
      "Executive Cabin",
      "Bank / ATM",
      "Kiosk",
      "Storage",
      "Jodi",
    ],
  },
  [PROPERTY_CATEGORIES.HYBRID]: {
    category: PROPERTY_CATEGORIES.HYBRID,
    label: "Hybrid (Residential + Commercial)",
    subdivisionLabel: "Tower / Wing / Block",
    subdivisionPlural: "Towers / Wings / Blocks",
    levelLabel: "Floor",
    unitLabel: "Unit (Flat/Shop/Office)",
    unitPlural: "Units",
    defaultStartFloor: 0,
    supportsGroundFloor: true,
    supportsBasements: true,
    defaultUnitType: "Shop",
    standardUnitTypes: [
      "Shop",
      "Retail Store",
      "Showroom",
      "Commercial Office",
      "1BHK",
      "2BHK",
      "3BHK",
      "4BHK",
      "Studio",
      "Penthouse",
      "Cabin",
      "Storage",
      "Jodi",
    ],
  },
  [PROPERTY_CATEGORIES.INDUSTRIAL]: {
    category: PROPERTY_CATEGORIES.INDUSTRIAL,
    label: "Industrial",
    subdivisionLabel: "Wing / Block",
    subdivisionPlural: "Wings / Blocks",
    levelLabel: "Floor",
    unitLabel: "Workspace / Desk",
    unitPlural: "Workspaces",
    defaultStartFloor: 0,
    supportsGroundFloor: true,
    supportsBasements: true,
    defaultUnitType: "—",
    standardUnitTypes: [
      "—",
      "IT",
      "Operations",
      "Sales",
      "Marketing",
      "HR",
      "Finance",
      "Executive",
      "Engineering",
    ],
  },
};

/**
 * Normalizes any property type string to one of the 4 defined categories.
 */
export const normalizePropertyType = (rawType) => {
  const str = String(rawType || "").trim().toLowerCase();
  if (!str) return PROPERTY_CATEGORIES.RESIDENTIAL;

  if (str.includes("hybrid") || (str.includes("res") && str.includes("com"))) {
    return PROPERTY_CATEGORIES.HYBRID;
  }
  if (str.includes("commercial")) {
    return PROPERTY_CATEGORIES.COMMERCIAL;
  }
  if (str.includes("industrial") || str.includes("factory") || str.includes("plant")) {
    return PROPERTY_CATEGORIES.INDUSTRIAL;
  }
  return PROPERTY_CATEGORIES.RESIDENTIAL;
};

/**
 * Retrieves the structure configuration for a given property type string.
 */
export const getStructureConfigByPropertyType = (propertyType) => {
  const normalized = normalizePropertyType(propertyType);
  return PROPERTY_STRUCTURE_CONFIG[normalized] || PROPERTY_STRUCTURE_CONFIG[PROPERTY_CATEGORIES.RESIDENTIAL];
};

/**
 * Formats a numeric floor into a human-friendly display name.
 * 0  => "Ground Floor (G)" or "G"
 * -1 => "Basement 1 (B1)" or "B1"
 * 1  => "Floor 1" or "F1"
 */
export const formatFloorName = (floorNumber, { short = false } = {}) => {
  const num = Number(floorNumber);
  if (!Number.isFinite(num)) return `Floor ${floorNumber ?? "-"}`;

  if (num === 0) {
    return short ? "G" : "Ground Floor (G)";
  }
  if (num < 0) {
    const bNum = Math.abs(num);
    return short ? `B${bNum}` : `Basement ${bNum} (B${bNum})`;
  }
  return short ? `F${num}` : `Floor ${num}`;
};

/**
 * Parses user-entered floor strings (e.g. "G", "Ground", "B1", "-1", "2") to a numeric floor.
 */
export const parseFloorNumber = (value) => {
  if (typeof value === "number" && Number.isInteger(value)) return value;
  const str = String(value || "").trim().toUpperCase();
  if (str === "G" || str === "GROUND" || str === "0") return 0;
  if (str.startsWith("B")) {
    const bVal = parseInt(str.replace(/\D/g, ""), 10);
    return Number.isFinite(bVal) && bVal > 0 ? -bVal : -1;
  }
  const parsed = parseInt(str, 10);
  return Number.isFinite(parsed) ? parsed : NaN;
};

/**
 * Generates badge style { backgroundColor, color, border } tailored to unit type.
 */
export const getUnitTypeBadgeStyle = (unitType) => {
  const str = String(unitType || "").trim().toLowerCase();

  // Merged / Jodi
  if (str === "jodi" || str === "merged" || str.includes("+")) {
    return {
      backgroundColor: "#fee2e2",
      color: "#b91c1c",
      border: "1px solid #fecaca",
    };
  }

  // Commercial: Offices, Cabins, Suites
  if (str.includes("office") || str.includes("cabin") || str.includes("suite") || str.includes("desk")) {
    return {
      backgroundColor: "#eff6ff",
      color: "#1d4ed8",
      border: "1px solid #bfdbfe",
    };
  }

  // Commercial: Shops, Showrooms, Retail
  if (str.includes("shop") || str.includes("showroom") || str.includes("retail") || str.includes("stall")) {
    return {
      backgroundColor: "#fef3c7",
      color: "#b45309",
      border: "1px solid #fde68a",
    };
  }

  // Industrial: Galas, Sheds
  if (str.includes("gala") || str.includes("shed") || str.includes("plot") || str.includes("yard")) {
    return {
      backgroundColor: "#f8fafc",
      color: "#334155",
      border: "1px solid #cbd5e1",
    };
  }

  // Industrial: Warehouses, Godowns, Cold Storage
  if (str.includes("warehouse") || str.includes("godown") || str.includes("storage")) {
    return {
      backgroundColor: "#ecfdf5",
      color: "#047857",
      border: "1px solid #a7f3d0",
    };
  }

  // Industrial: Manufacturing, Workshops, Bays, Labs
  if (str.includes("manufacturing") || str.includes("workshop") || str.includes("bay") || str.includes("lab")) {
    return {
      backgroundColor: "#fff7ed",
      color: "#c2410c",
      border: "1px solid #fed7aa",
    };
  }

  // Residential: 1BHK
  if (str.includes("1bhk") || str.includes("studio")) {
    return {
      backgroundColor: "#fef3c7",
      color: "#b45309",
      border: "1px solid #fde68a",
    };
  }

  // Residential: 2BHK
  if (str.includes("2bhk")) {
    return {
      backgroundColor: "#ccfbf1",
      color: "#0f766e",
      border: "1px solid #99f6e4",
    };
  }

  // Residential: 3BHK
  if (str.includes("3bhk")) {
    return {
      backgroundColor: "#e0e7ff",
      color: "#4338ca",
      border: "1px solid #c7d2fe",
    };
  }

  // Residential: 4BHK, 5BHK, Penthouse, Duplex, Villa
  if (str.includes("4bhk") || str.includes("5bhk") || str.includes("penthouse") || str.includes("duplex") || str.includes("villa") || str.includes("row house")) {
    return {
      backgroundColor: "#fae8ff",
      color: "#86198f",
      border: "1px solid #f5d0fe",
    };
  }

  // Default fallback
  return {
    backgroundColor: "#f1f5f9",
    color: "#475569",
    border: "1px solid #e2e8f0",
  };
};

/**
 * Extracts a standardized 2-4 letter Department Code (e.g. IT, HR, FIN, SAL, MKT, DIR, REC).
 */
export const getDepartmentShortCode = (name = "", unitType = "", flatNumber = "") => {
  const combined = ` ${name} ${unitType} ${flatNumber} `.toUpperCase();
  if (combined.includes(" DEV") || combined.includes("DEVELOPMENT") || combined.includes("SOFTWARE") || combined.includes("-DEV-") || combined.includes(" DEV-") || combined.includes("-DEV ")) return "DEV";
  if (combined.includes(" TECH") || combined.includes("ENGINEERING") || combined.includes(" IT ") || combined.includes("INFORMATION") || combined.includes("-IT-")) return "IT";
  if (combined.includes(" HR ") || combined.includes("HUMAN RESOURCE") || combined.includes("-HR-")) return "HR";
  if (combined.includes("FINANCE") || combined.includes("ACCOUNT") || combined.includes(" CFO") || combined.includes("-FIN-") || combined.includes("-ACC-")) return "FIN";
  if (combined.includes("SALES") || combined.includes("BUSINESS DEV") || combined.includes(" CRM") || combined.includes("-SAL-") || combined.includes("-BIZ-")) return "SAL";
  if (combined.includes("MARKETING") || combined.includes("CREATIVE") || combined.includes("DESIGN")) return "MKT";
  if (combined.includes("LEGAL") || combined.includes("LIAISON") || combined.includes("LAND")) return "LEG";
  if (combined.includes("CIVIL") || combined.includes("ARCHITECTURE") || combined.includes("BILLING")) return "CIV";
  if (combined.includes("PURCHASE") || combined.includes("PROCUREMENT")) return "PUR";
  if (combined.includes("MEDIA") || combined.includes("PUBLIC RELATIONS") || combined.includes(" PR ")) return "MED";
  if (combined.includes("OPERATIONS") || combined.includes("FACILITY") || combined.includes(" OPS ")) return "OPS";
  if (combined.includes("SECURITY") || combined.includes("GUARD")) return "SEC";
  if (combined.includes("RECEPTION") || combined.includes("FRONT DESK")) return "REC";
  if (combined.includes("DIRECTOR") || combined.includes("EXECUTIVE") || combined.includes("BOARD")) return "DIR";
  if (combined.includes("PANTRY") || combined.includes("CAFETERIA") || combined.includes("KITCHEN")) return "PAN";
  if (combined.includes("ORGANIC")) return "ORG";
  if (combined.includes("ADMIN")) return "ADM";
  if (combined.includes(" QA ") || combined.includes("QUALITY") || combined.includes("TESTING") || combined.includes("-QA-")) return "QA";
  return "DEPT";
};

/**
 * Builds an industrial-grade Team Hierarchy Tree and workstation roster from unit assignments.
 * NOTE: "designation is not a role it is a field for data."
 * Desk codes format: easy-to-understand codes (e.g. IT-TL-123, IT-DEV-01, FIN-CFO-01).
 */
export const buildIndustrialTeamTree = (assignments = []) => {
  if (!Array.isArray(assignments) || !assignments.length) {
    return { rootNodes: [], allMembers: [], totalMembers: 0, totalDesks: 0 };
  }

  const members = [];
  assignments.forEach((a) => {
    const u = a.user || (a.user_id ? { user_id: a.user_id, full_name: `User #${a.user_id}` } : null);
    if (!u) return;

    const rd = u.role_details || {};
    const designation = rd.designation || rd.title || ""; // Strictly a data field!
    const floorNumber = rd.floor_number ?? u.floor_number ?? a.floor_number ?? null;
    const flatId = rd.flat_id ?? u.flat_id ?? a.flat_id ?? null;
    const towerId = rd.tower_id ?? u.tower_id ?? a.tower_id ?? null;
    const deskCode = rd.desk_code || rd.workstation_code || rd.desk || u.desk_code || a.flat_number || "";
    const department = rd.department || rd.dept_name || "";
    const reportsToId = rd.reports_to_id ? Number(rd.reports_to_id) : null;
    const reportsToName = rd.reports_to_name || "";
    const isLead = Boolean(rd.is_lead) || 
      String(u.primary_role || "").includes("Lead") || 
      String(u.primary_role || "").includes("Head") || 
      String(u.primary_role || "").includes("Director");

    members.push({
      user_id: u.user_id,
      full_name: u.full_name || "Staff Member",
      email_id: u.email_id || "",
      mobile_number: u.mobile_number || "",
      primary_role: u.primary_role || "Employee",
      system_role: u.system_role || u.primary_role || "Employee",
      designation, // Data field
      desk_code: deskCode, // Standard Desk Code (e.g. IT-TL-123)
      cabin_code: rd.cabin_code || "",
      floor_number: floorNumber,
      flat_id: flatId,
      tower_id: towerId,
      delegate: rd.delegate || null,
      department,
      reports_to_id: reportsToId,
      reports_to_name: reportsToName,
      is_lead: isLead,
      duty_status: rd.duty_status || (u.is_active ? "On Duty" : "Off Duty"),
      children: [],
    });
  });

  const memberMap = new Map();
  members.forEach((m) => memberMap.set(m.user_id, { ...m, children: [] }));

  const rootNodes = [];
  const attachedIds = new Set();

  // First pass: link children to parent in this team
  memberMap.forEach((member) => {
    if (member.reports_to_id && memberMap.has(member.reports_to_id) && member.reports_to_id !== member.user_id) {
      const parent = memberMap.get(member.reports_to_id);
      parent.children.push(member);
      attachedIds.add(member.user_id);
    }
  });

  // Second pass: roots are those not attached as children
  memberMap.forEach((member) => {
    if (!attachedIds.has(member.user_id)) {
      rootNodes.push(member);
    }
  });

  // Sort roots: Directors -> HODs -> TLs -> Staff
  const roleRank = (role, isLead) => {
    const r = String(role || "").toLowerCase();
    if (r.includes("director")) return 1;
    if (r.includes("head") || r.includes("hod")) return 2;
    if (r.includes("lead") || isLead) return 3;
    if (r.includes("reception")) return 4;
    return 5;
  };

  rootNodes.sort((a, b) => roleRank(a.system_role, a.is_lead) - roleRank(b.system_role, b.is_lead));

  const deskEligibleMembers = members.filter((m) => !isNonDeskRole(m.system_role, m.designation));
  const assignedDesksCount = members.filter((m) => Boolean(m.desk_code)).length;
  const totalDesks = assignedDesksCount > 0 ? assignedDesksCount : deskEligibleMembers.length;

  return {
    rootNodes,
    allMembers: members,
    totalMembers: members.length,
    totalDesks,
    deskEligibleCount: deskEligibleMembers.length,
  };
};

/**
 * Identifies operational and support roles that do not occupy workstations or desks
 * (e.g., Cleaning staff, Housekeeping, Security Guards, Drivers, Pantry helpers).
 */
export const isNonDeskRole = (role = "", designation = "") => {
  const r = `${role || ""} ${designation || ""}`.toLowerCase();
  return (
    r.includes("clean") ||
    r.includes("housekeep") ||
    r.includes("pantry") ||
    r.includes("guard") ||
    r.includes("security") ||
    r.includes("maintenance") ||
    r.includes("peon") ||
    r.includes("helper") ||
    r.includes("driver")
  );
};

/**
 * Categorizes commercial shop staff into store manager, cashiers, sales reps, accountants, etc.
 * NOTE: Commercial shops do NOT use desk codes.
 */
export const buildCommercialStaffRoster = (assignments = []) => {
  if (!Array.isArray(assignments) || !assignments.length) {
    return {
      manager: null,
      cashiers: [],
      sales: [],
      accountants: [],
      receptionists: [],
      support: [],
      allStaff: [],
      totalStaff: 0,
    };
  }

  const roster = {
    manager: null,
    cashiers: [],
    sales: [],
    accountants: [],
    receptionists: [],
    support: [],
    allStaff: [],
    totalStaff: 0,
  };

  assignments.forEach((a) => {
    const u = a.user || (a.user_id ? { user_id: a.user_id, full_name: `User #${a.user_id}` } : null);
    if (!u) return;

    const rd = u.role_details || {};
    const designation = rd.designation || rd.title || "";
    const roleStr = `${u.primary_role || ""} ${u.system_role || ""} ${designation}`.toLowerCase();

    const member = {
      user_id: u.user_id,
      full_name: u.full_name || "Staff Member",
      email_id: u.email_id || "",
      mobile_number: u.mobile_number || "",
      primary_role: u.primary_role || "Staff",
      system_role: u.system_role || u.primary_role || "Staff",
      designation,
      duty_status: rd.duty_status || (u.is_active ? "On Duty" : "Off Duty"),
    };

    roster.allStaff.push(member);

    if (roleStr.includes("manager") || roleStr.includes("owner") || roleStr.includes("franchisee") || roleStr.includes("lead")) {
      if (!roster.manager) roster.manager = member;
      else roster.sales.push(member);
    } else if (roleStr.includes("cashier") || roleStr.includes("billing") || roleStr.includes("pos")) {
      roster.cashiers.push(member);
    } else if (roleStr.includes("account") || roleStr.includes("finance") || roleStr.includes("audit")) {
      roster.accountants.push(member);
    } else if (roleStr.includes("reception") || roleStr.includes("front desk") || roleStr.includes("greeter")) {
      roster.receptionists.push(member);
    } else if (roleStr.includes("sale") || roleStr.includes("store executive") || roleStr.includes("associate")) {
      roster.sales.push(member);
    } else {
      roster.support.push(member);
    }
  });

  roster.totalStaff = roster.allStaff.length;
  return roster;
};

/**
 * Returns dynamic navigation tabs based on property type and enabled modules.
 */
export const getPropertyNavigationTabs = (propertyType, badgeCounts = {}) => {
  const normalized = normalizePropertyType(propertyType);
  const approvalsBadge =
    badgeCounts.approvals && Number(badgeCounts.approvals) > 0
      ? Number(badgeCounts.approvals)
      : null;

  if (normalized === PROPERTY_CATEGORIES.INDUSTRIAL) {
    return [
      { id: "towers", label: "Buildings & Depts", icon: "Building2" },
      { id: "visits", label: "Visitor Passes", icon: "QrCode" },
      { id: "network", label: "Delivery & Cab Network", icon: "Truck" },
      { id: "industrial-settings", label: "Intake & Timeouts", icon: "Sliders" },
      { id: "approvals", label: "Staff Approvals", badge: approvalsBadge, icon: "UserCheck" },
      { id: "directories", label: "Employee Directory", icon: "Users" },
      { id: "chats", label: "Internal Chat", icon: "MessageSquare" },
      { id: "support", label: "Helpdesk & Tickets", icon: "LifeBuoy" },
      { id: "testers", label: "Tester Users", icon: "FlaskConical" },
      { id: "gates", label: "Gate & Loading Bays", icon: "Shield" },
      { id: "staff", label: "Operations & Security", icon: "Briefcase" },
      { id: "documents", label: "Safety & SOPs", icon: "FileText" },
      { id: "settings", label: "Settings & Modules", icon: "Settings" },
    ];
  }

  if (normalized === PROPERTY_CATEGORIES.COMMERCIAL) {
    return [
      { id: "towers", label: "Wings & Floors", icon: "Building2" },
      { id: "leadership", label: "Store & Floor Mgmt", icon: "Award" },
      { id: "approvals", label: "Tenant Approvals", badge: approvalsBadge, icon: "UserCheck" },
      { id: "directories", label: "Store Directory", icon: "Store" },
      { id: "chats", label: "Commercial Chat", icon: "MessageSquare" },
      { id: "support", label: "Support Tickets", icon: "LifeBuoy" },
      { id: "testers", label: "Tester Users", icon: "FlaskConical" },
      { id: "gates", label: "Gates & Deliveries", icon: "Shield" },
      { id: "staff", label: "Facility Staff", icon: "Briefcase" },
      { id: "documents", label: "Lease & Policies", icon: "FileText" },
      { id: "settings", label: "Settings & Modules", icon: "Settings" },
    ];
  }

  if (normalized === PROPERTY_CATEGORIES.HYBRID) {
    return [
      { id: "towers", label: "Towers & Commercial", icon: "Building2" },
      { id: "leadership", label: "Joint Committee", icon: "Award" },
      { id: "approvals", label: "Member Approvals", badge: approvalsBadge, icon: "UserCheck" },
      { id: "directories", label: "Resident & Business Directory", icon: "Users" },
      { id: "chats", label: "Community Chat", icon: "MessageSquare" },
      { id: "support", label: "Support Tickets", icon: "LifeBuoy" },
      { id: "testers", label: "Tester Users", icon: "FlaskConical" },
      { id: "gates", label: "Gates", icon: "Shield" },
      { id: "staff", label: "Staff", icon: "Briefcase" },
      { id: "documents", label: "Documents", icon: "FileText" },
      { id: "settings", label: "Settings & Modules", icon: "Settings" },
    ];
  }

  // Residential default & Custom Archetypes
  return [
    { id: "towers", label: "Towers", icon: "Building2" },
    { id: "leadership", label: "Committee", icon: "Award" },
    { id: "approvals", label: "Approvals", badge: approvalsBadge, icon: "UserCheck" },
    { id: "directories", label: "Directories", icon: "Users" },
    { id: "chats", label: "Society Chat", icon: "MessageSquare" },
    { id: "support", label: "Support Tickets", icon: "LifeBuoy" },
    { id: "testers", label: "Tester Users", icon: "FlaskConical" },
    { id: "gates", label: "Gates", icon: "Shield" },
    { id: "staff", label: "Staff", icon: "Briefcase" },
    { id: "documents", label: "Documents", icon: "FileText" },
    { id: "settings", label: "Settings", icon: "Settings" },
  ];
};

/**
 * Returns dynamic role and leadership management configuration based on property type.
 */
export const getPropertyLeadershipConfig = (propertyType) => {
  const normalized = normalizePropertyType(propertyType);

  if (normalized === PROPERTY_CATEGORIES.INDUSTRIAL) {
    return {
      title: "Executive Leadership & Department HODs",
      subtitle: "Assign leadership, HOD, and delegation authorities to verified employees & executives.",
      assignButtonText: "Assign Leadership Role",
      modalTitle: "Assign Leadership Role",
      modalDescription: "Select an employee to elevate to department leadership, executive directorate, or floor receptionist.",
      userSelectLabel: "Select Employee",
      userPlaceholder: "-- Choose Employee --",
      roleFieldLabel: "Designation / Role",
      roles: [
        { value: "Director", label: "Director / Executive Board" },
        { value: "Head of Department (HOD)", label: "Head of Department (HOD)" },
        { value: "General Manager", label: "General Manager (GM)" },
        { value: "Operations Lead", label: "Operations Lead" },
        { value: "Delegate", label: "Authorized Delegate" },
        { value: "Floor Receptionist", label: "Floor Receptionist" },
      ],
      unitLabel: "Cabin / Desk",
    };
  }

  if (normalized === PROPERTY_CATEGORIES.COMMERCIAL) {
    return {
      title: "Store & Floor Management",
      subtitle: "Assign facility, floor supervision, and store management authorities.",
      assignButtonText: "Assign Management Role",
      modalTitle: "Assign Store/Floor Role",
      modalDescription: "Select a tenant or business user to assign store/floor oversight.",
      userSelectLabel: "Select Store User / Staff",
      userPlaceholder: "-- Choose Store User --",
      roleFieldLabel: "Management Role",
      roles: [
        { value: "Building Manager", label: "Building Manager" },
        { value: "Floor Supervisor", label: "Floor Supervisor" },
        { value: "Store Manager", label: "Store Manager" },
        { value: "Security Supervisor", label: "Security Supervisor" },
      ],
      unitLabel: "Shop / Office",
    };
  }

  if (normalized === PROPERTY_CATEGORIES.HYBRID) {
    return {
      title: "Joint Management Committee",
      subtitle: "Assign administrative and oversight roles across residential towers and commercial spaces.",
      assignButtonText: "Assign Leadership Role",
      modalTitle: "Assign Joint Committee Role",
      modalDescription: "Select a resident or commercial member to elevate to joint management.",
      userSelectLabel: "Select Member",
      userPlaceholder: "-- Choose Member --",
      roleFieldLabel: "Committee / Leadership Role",
      roles: [
        { value: "Chairman", label: "Chairman" },
        { value: "Secretary", label: "Secretary" },
        { value: "Treasurer", label: "Treasurer" },
        { value: "Commercial Representative", label: "Commercial Arcade Lead" },
        { value: "Member", label: "General Member" },
      ],
      unitLabel: "Unit",
    };
  }

  // Residential default
  return {
    title: "Management Committee",
    subtitle: "Assign administrative roles to verified residents.",
    assignButtonText: "Assign Role",
    modalTitle: "Assign Committee Role",
    modalDescription: "Select a verified resident to elevate to the Management Committee.",
    userSelectLabel: "Select Resident",
    userPlaceholder: "-- Choose Resident --",
    roleFieldLabel: "Committee Role",
    roles: [
      { value: "Chairman", label: "Chairman" },
      { value: "Secretary", label: "Secretary" },
      { value: "Treasurer", label: "Treasurer" },
      { value: "Member", label: "General Member" },
    ],
    unitLabel: "Flat",
  };
};

